/**
 * Finanzas → **Contabilidad** (`/finanzas/contabilidad`).
 *
 * Puerto de `Concar::upload` / `Concar::process` y de las dos salidas de
 * `Prueba::index` con `tipo_cpe=concar` y `tipo_cpe=dbf` (vistas
 * `views/menu/concar/index.php` y `views/menu/concar/upload.php`).
 *
 * La pantalla tiene los tres pasos del puente con CONCAR, en el orden en que se
 * usan cada mes:
 *
 * 1. **Reporte de ventas** — se elige el mes y el documento (boletas o facturas) y
 *    se descarga el Excel con el asiento de cada comprobante ya armado.
 * 2. **Anexos de facturas** — el Excel con el RUC y la razón social de los
 *    clientes que facturaron, para darlos de alta en CONCAR.
 * 3. **Convertir a DBF** — se sube la plantilla (la que acaba de descargarse o una
 *    llenada a mano) y la API devuelve la cabecera y el detalle en `.dbf`.
 *
 * Reglas heredadas:
 * - El período es **mensual**: el mes y el año se eligen una vez y valen para los
 *   tres bloques.
 * - El reporte de ventas sólo trae ventas vigentes y arma tres asientos por
 *   comprobante (venta, IGV y cobro) con las cuentas del original.
 *
 * Diferencias anotadas:
 * - Antes había que pasar por tres pantallas distintas (el menú de Concar, el
 *   `Concar - Excel a DBF` y un `window.open` a `prueba?tipo=…`); ahora los tres
 *   pasos viven en la misma página y comparten el período.
 * - La conversión informa si el asiento **cuadra** (debe = haber) y avisa cuando no.
 * - Los nombres de los archivos ya no llevan el `/` del original
 *   (`CONCAR_FACTURA_1/2026.xls`).
 */

import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FileUp,
  Info,
} from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { ArchivoInput } from '@/components/ui/archivo-input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency } from '@/lib/utils';

import { contabilidadApi, nombreDeAnexos, nombreDeConcar } from '../contabilidad.api';
import type { DocumentoConcar, PeriodoConcar, ResultadoDbf } from '../contabilidad.types';

/** Tamaño legible de un archivo (los DBF generados). */
function tamanoLegible(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Los años que el original ofrecía en el desplegable. */
const ANIOS = Array.from({ length: 12 }, (_, indice) => 2019 + indice);

const HOY = new Date();

export function ContabilidadPage() {
  useDocumentTitle('Finanzas · Contabilidad');

  const [periodo, setPeriodo] = useState<PeriodoConcar>({
    mes: HOY.getMonth() + 1,
    anio: HOY.getFullYear(),
  });
  const [documento, setDocumento] = useState<DocumentoConcar>('FACTURA');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [resultado, setResultado] = useState<ResultadoDbf | null>(null);
  const [descargando, setDescargando] = useState<string | null>(null);
  const [convirtiendo, setConvirtiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mensajeDe = (fallo: unknown) =>
    fallo instanceof ApiError
      ? fallo.message
      : fallo instanceof Error
        ? fallo.message
        : 'No se pudo completar la operación.';

  /** Descarga autenticada (la API pide sesión también para los Excel). */
  const descargar = async (clave: string, url: string, nombre: string) => {
    setError(null);
    setDescargando(clave);

    try {
      await abrirPdfAutenticado(url, { nombre });
    } catch (fallo) {
      setError(mensajeDe(fallo));
    } finally {
      setDescargando(null);
    }
  };

  const convertir = async () => {
    if (!archivo) return;

    setError(null);
    setResultado(null);
    setConvirtiendo(true);

    try {
      setResultado(await contabilidadApi.convertir(archivo));
    } catch (fallo) {
      setError(mensajeDe(fallo));
    } finally {
      setConvirtiendo(false);
    }
  };

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold text-ink-900">Contabilidad</h1>
        <p className="mt-1 text-sm text-ink-500">
          Los tres pasos del puente con CONCAR: el reporte de ventas del mes, los anexos de las
          facturas y la conversión de la plantilla al archivo DBF que se importa.
        </p>
      </header>

      {error ? (
        <Alert variant="error" title="No se pudo completar la operación">
          {error}
        </Alert>
      ) : null}

      {/* Período, compartido por los tres bloques */}
      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-4">
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Mes
            <Select
              value={String(periodo.mes)}
              onChange={(evento) =>
                setPeriodo((actual) => ({ ...actual, mes: Number(evento.target.value) }))
              }
              className={CONTROL}
              aria-label="Mes"
            >
              {MESES.map((nombre, indice) => (
                <option key={nombre} value={String(indice + 1)}>
                  {nombre}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Año
            <Select
              value={String(periodo.anio)}
              onChange={(evento) =>
                setPeriodo((actual) => ({ ...actual, anio: Number(evento.target.value) }))
              }
              className={CONTROL}
              aria-label="Año"
            >
              {ANIOS.map((anio) => (
                <option key={anio} value={String(anio)}>
                  {anio}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Tipo de comprobante
            <Select
              value={documento}
              onChange={(evento) => setDocumento(evento.target.value as DocumentoConcar)}
              className={CONTROL}
              aria-label="Tipo de comprobante"
            >
              <option value="FACTURA">Facturas</option>
              <option value="BOLETA">Boletas</option>
            </Select>
          </label>

          <div className="flex flex-col justify-end">
            <span className="text-xs text-ink-500">
              Período {String(periodo.mes).padStart(2, '0')}-{periodo.anio}
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {/* 1. Reporte de ventas */}
        <Card>
          <CardContent className="flex h-full flex-col gap-3 p-4">
            <div className="flex items-start gap-3">
              <span className="rounded-control bg-brand-50 p-2 text-brand-600">
                <FileSpreadsheet className="size-5" aria-hidden="true" />
              </span>
              <div>
                <h2 className="text-base font-semibold text-ink-900">Reporte de ventas</h2>
                <p className="text-sm text-ink-500">
                  Excel con el asiento de cada {documento === 'FACTURA' ? 'factura' : 'boleta'} del
                  mes: la venta, el IGV y el cobro.
                </p>
              </div>
            </div>

            <ul className="space-y-1 text-xs text-ink-500">
              <li>
                Cuentas: venta <code className="font-mono">701111</code>, IGV{' '}
                <code className="font-mono">401111</code> y cobro{' '}
                <code className="font-mono">{documento === 'FACTURA' ? '121201' : '121203'}</code>.
              </li>
              <li>
                Subdiario {documento === 'FACTURA' ? '05' : '04'} y anexo{' '}
                {documento === 'FACTURA' ? 'el RUC del cliente' : 'genérico 0002'}.
              </li>
              <li>Una hoja con las tres filas de especificación de campos de CONCAR.</li>
            </ul>

            <div className="mt-auto">
              <Button
                leftIcon={<Download className="size-4" aria-hidden="true" />}
                loading={descargando === 'concar'}
                onClick={() =>
                  void descargar(
                    'concar',
                    contabilidadApi.urlConcar(periodo, documento),
                    nombreDeConcar(periodo, documento),
                  )
                }
              >
                Descargar Excel de ventas
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 2. Anexos */}
        <Card>
          <CardContent className="flex h-full flex-col gap-3 p-4">
            <div className="flex items-start gap-3">
              <span className="rounded-control bg-sky-50 p-2 text-sky-600">
                <FileSpreadsheet className="size-5" aria-hidden="true" />
              </span>
              <div>
                <h2 className="text-base font-semibold text-ink-900">Anexos de facturas</h2>
                <p className="text-sm text-ink-500">
                  Excel con el RUC y la razón social de los clientes que facturaron en el mes.
                </p>
              </div>
            </div>

            <ul className="space-y-1 text-xs text-ink-500">
              <li>
                Un renglón por RUC (con 11 dígitos), con estado{' '}
                <code className="font-mono">V</code> y moneda{' '}
                <code className="font-mono">MN</code>.
              </li>
              <li>Columnas AVANEXO, ACODANE, ADESANE, ARUC, AESTADO, ADATE y AHORA.</li>
              <li>Para dar de alta los anexos antes de importar los asientos.</li>
            </ul>

            <div className="mt-auto">
              <Button
                variant="outline"
                leftIcon={<Download className="size-4" aria-hidden="true" />}
                loading={descargando === 'anexos'}
                onClick={() =>
                  void descargar(
                    'anexos',
                    contabilidadApi.urlAnexos(periodo),
                    nombreDeAnexos(periodo),
                  )
                }
              >
                Descargar Excel de anexos
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 3. Excel → DBF */}
      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex items-start gap-3">
            <span className="rounded-control bg-emerald-50 p-2 text-emerald-600">
              <FileUp className="size-5" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-base font-semibold text-ink-900">Convertir Excel a DBF</h2>
              <p className="text-sm text-ink-500">
                Sube la plantilla de CONCAR (la que acabas de descargar o una llenada a mano) y se
                genera la <strong>cabecera</strong> y el <strong>detalle</strong> de comprobantes en
                DBF.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3 rounded-card border border-dashed border-ink-200 p-3">
            <ArchivoInput
              className="w-full min-w-56 flex-1"
              label="Archivo Excel (.xlsx, .xls)"
              accept=".xlsx,.xls"
              nombre={archivo?.name ?? null}
              onSeleccionar={(elegido) => {
                setArchivo(elegido?.archivo ?? null);
                setResultado(null);
              }}
            />

            <Button
              leftIcon={<FileUp className="size-4" aria-hidden="true" />}
              disabled={!archivo}
              loading={convirtiendo}
              onClick={() => void convertir()}
            >
              Convertir a DBF
            </Button>
          </div>

          {resultado ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-card border border-ink-100 p-3">
                  <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    Comprobantes
                  </p>
                  <p className="text-lg font-bold text-ink-900 tabular-nums">
                    {resultado.comprobantes.toLocaleString('es-PE')}
                  </p>
                </div>
                <div className="rounded-card border border-ink-100 p-3">
                  <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    Asientos (detalle)
                  </p>
                  <p className="text-lg font-bold text-ink-900 tabular-nums">
                    {resultado.detalles.toLocaleString('es-PE')}
                  </p>
                </div>
                <div className="rounded-card border border-ink-100 p-3">
                  <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    Debe
                  </p>
                  <p className="text-lg font-bold break-words text-ink-900 tabular-nums">
                    {formatCurrency(resultado.debe)}
                  </p>
                </div>
                <div className="rounded-card border border-ink-100 p-3">
                  <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    Haber
                  </p>
                  <p className="text-lg font-bold break-words text-ink-900 tabular-nums">
                    {formatCurrency(resultado.haber)}
                  </p>
                </div>
              </div>

              {resultado.cuadrado ? (
                <Alert variant="success" title="El asiento cuadra">
                  El debe y el haber coinciden: los DBF se pueden importar en CONCAR.
                </Alert>
              ) : (
                <Alert variant="warning" title="El asiento no cuadra">
                  El debe y el haber no coinciden ({formatCurrency(resultado.debe)} contra{' '}
                  {formatCurrency(resultado.haber)}). Revisa el Excel antes de importar los DBF en
                  CONCAR.
                </Alert>
              )}

              <div className="flex flex-wrap gap-2">
                {resultado.archivos.map((archivoDbf) => (
                  <Button
                    key={archivoDbf.nombre}
                    variant="outline"
                    leftIcon={<Download className="size-4" aria-hidden="true" />}
                    loading={descargando === archivoDbf.nombre}
                    onClick={() =>
                      void descargar(
                        archivoDbf.nombre,
                        contabilidadApi.urlDbf(archivoDbf.nombre),
                        archivoDbf.nombre,
                      )
                    }
                  >
                    {archivoDbf.titulo} ({tamanoLegible(archivoDbf.bytes)})
                  </Button>
                ))}
              </div>
            </div>
          ) : null}

          <p className="flex items-start gap-2 text-xs text-ink-500">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            Se aceptan las dos formas de plantilla: la que lleva delante la columna «Campo» (la que
            genera esta pantalla) y la que empieza directamente en el subdiario. El asiento se corta
            a la longitud de cada campo de CONCAR, así que conviene revisar los avisos antes de
            importar.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <p className="flex items-start gap-2 text-xs text-ink-500">
          <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
          El reporte de ventas sólo incluye ventas vigentes del mes elegido, con el correlativo del
          comprobante en el orden en que se emitieron.
        </p>
        <p className="flex items-start gap-2 text-xs text-ink-500">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-500" aria-hidden="true" />
          Un período con muchísimos comprobantes tarda unos segundos en armarse; si el Excel pasa de
          20 MB, conviértelo por meses.
        </p>
      </div>
    </div>
  );
}
